import React, { useState } from "react";
import { Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { restaurantApi, errorMessage } from "../../lib/api";
import ImageField from "./ImageField";
import LocationPicker from "./LocationPicker";
import CuisineSelect from "./CuisineSelect";

export default function RestaurantProfileForm({ restaurant, onSaved, title = "Restaurant profile" }) {
  const [form, setForm] = useState({
    name: restaurant?.name || "", cuisines: restaurant?.cuisines || (restaurant?.cuisine ? [restaurant.cuisine] : []), address: restaurant?.address || "", description: restaurant?.description || "",
    image: restaurant?.image || "", delivery_fee: restaurant?.delivery_fee ?? 30, min_order: restaurant?.min_order ?? 99, is_open: restaurant?.is_open ?? true,
    lat: restaurant?.lat ?? 25.6751, lng: restaurant?.lng ?? 94.1086,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const saved = await restaurantApi.save({ ...form, delivery_fee: Number(form.delivery_fee), min_order: Number(form.min_order), lat: Number(form.lat), lng: Number(form.lng), image: form.image || null, description: form.description || null });
      toast.success("Restaurant saved");
      onSaved?.(saved);
    } catch (err) { toast.error(errorMessage(err, "Could not save")); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="kt-card p-6" data-testid="restaurant-profile-form">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand"><Store className="h-5 w-5" /></span>
        <div><h2 className="text-lg font-semibold text-ink">{title}</h2><p className="text-sm text-ink-soft">{restaurant ? "Update how customers see your restaurant." : "Set up your restaurant to start receiving orders."}</p></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Restaurant name</Label><Input required value={form.name} onChange={set("name")} placeholder="e.g. Hornbill Kitchen" data-testid="rest-name" /></div>
        <div className="sm:col-span-2"><CuisineSelect value={form.cuisines} onChange={(v) => setForm((f) => ({ ...f, cuisines: v }))} /></div>
        <div className="space-y-2 sm:col-span-2"><Label>Address</Label><Input required value={form.address} onChange={set("address")} placeholder="Street, locality, city" data-testid="rest-address" /></div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Exact location on map</Label>
          <p className="-mt-1 text-xs text-ink-soft">Search your address, then drag the pin to your exact doorstep so delivery drivers and customers can find you.</p>
          <LocationPicker
            value={{ lat: Number(form.lat), lng: Number(form.lng), address: form.address }}
            onChange={({ lat, lng, address }) => setForm((f) => ({ ...f, lat, lng, address: address || f.address }))}
          />
        </div>
        <div className="space-y-2 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={set("description")} placeholder="What makes your food special?" data-testid="rest-description" /></div>
        <div className="space-y-2 sm:col-span-2"><Label>Cover image</Label><ImageField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testId="rest-image" /></div>
        <div className="space-y-2 sm:col-span-2"><Label>Minimum order (INR)</Label><Input type="number" min="0" step="1" value={form.min_order} onChange={set("min_order")} data-testid="rest-min" /></div>
        <div className="flex items-center justify-between rounded-xl bg-surface-100 px-4 py-3 sm:col-span-2">
          <div><p className="text-sm font-medium text-ink">Accepting orders</p><p className="text-xs text-ink-soft">Turn off to temporarily close.</p></div>
          <Switch checked={!!form.is_open} onCheckedChange={(v) => setForm((f) => ({ ...f, is_open: v }))} data-testid="rest-open" />
        </div>
      </div>
      <Button type="submit" disabled={busy} className="mt-6 h-11 rounded-full bg-brand px-6 text-white hover:bg-brand-600" data-testid="rest-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {restaurant ? "Save changes" : "Create restaurant"}</Button>
    </form>
  );
}
